import { Navigate as RouterNavigate, useLocation } from 'react-router';
import { useAuth } from '../../auth/useAuth.js';
import { lazyNamed } from '../../lib/lazyRoute.js';

// בקובץ משלו (02/11/2026): `RootRedirect` יושב בקובץ הראשי, והדשבורד הכבד לא.
const DashboardPage = lazyNamed(() => import('./DashboardPage.js'), 'DashboardPage');

// הועבר מ-App.tsx ב-26/09/2026 כדי שאפשר יהיה לבדוק את הנחיתה לפי תפקיד
// בלי לטעון את כל האפליקציה.
// The dashboard is desktop-dense; a VIEWER (often on a phone) lands on the
// compact read-only glance view instead.
export function RoleLanding(): JSX.Element {
  const { user: authUser } = useAuth();
  // הנחיתה לפי התפקיד האמיתי: גישה מלאה (FULL_ACCESS) פותחת הרשאות של מנכ״ל,
  // אבל הבית נשאר של התפקיד — מדינה (רכש) נחתה על לוח הבקרה (אלי 05/10).
  const user = authUser ? { ...authUser, role: authUser.baseRole ?? authUser.role } : authUser;
  // מיילים על קוד האישור (`/app?security=code`) נוחתים כאן; ההפניה לפי תפקיד לא
  // זורקת את הפרמטר — חלון הקוד (SecurityHost במעטפת) נפתח במסך הנחיתה.
  const security = new URLSearchParams(useLocation().search).get('security');
  const go = (to: string): JSX.Element => (
    <RouterNavigate replace
      to={security ? `${to}${to.includes('?') ? '&' : '?'}security=${encodeURIComponent(security)}` : to} />
  );
  // ── עובד ייצור נוחת על העמדות ──────────────────────────────────────────
  //
  // Eli, 2026-09-02: "משתמש לעובד ייצור... איתו נכנסים לכל עמדת עבודה." The
  // dashboard below would answer 403 for every tile it draws, so the landing is
  // the station itself — the screen the tablet is actually for.
  //
  // ⚠ 26/09/2026: הנחיתה היא „בחר עמדה" ולא עמדת העבודה — אלי: „זה קיוסק
  // הרי, יתקע שם". וההפניה הזו לבדה מעולם לא רצה: השער של המעטפת
  // (RouteRoleGate) סירב ל-/app לפניה. ההפניה האמיתית יושבת עכשיו בשער.
  if (user?.role === 'PRODUCTION_WORKER') return go("/scan/choose");
  // „מבט מהיר“ היה מסך קריאה-בלבד לטלפון; לשונית „עכשיו“ בלוח הבקרה עונה על
  // אותה שאלה ומראה גם את מי שאינו עובד ואת המכונות — ונמדדה ב-390px בלי
  // גלישה. פעולות התור מוגנות ב-`hasRoleFor('DISPATCH')`, כך שצופה רואה
  // רשימה ולא כפתורים שייתנו לו 403.
  if (user?.role === 'VIEWER') return go("/app/production-center?tab=now");
  if (user?.role === 'ENGINEER') return go("/app/engineering/my-work");
  if (user?.role === 'ENG_MANAGER') return go("/app/engineering");
  // מנהל ההתקנות נוחת על לוח ההתקנות (משימה 12c, 25/09/2026).
  if (user?.role === 'SERVICE_MGR') return go("/app/installations");
  if (user?.role === 'PURCHASER') return go("/app/purchasing");
  // המחסנאי (30/09/2026) נוחת על מסך העבודה שלו — נתיבים במחשב, תור בטלפון.
  // ומאז 05/10/2026 — המקום האחד של המחסן (docs/warehouse-one-place-spec.md).
  if (user?.role === 'WAREHOUSE') return go("/app/warehouse");
  // מנהל הפרויקט נוחת על לוח ניהול הפרויקט — הבית המאושר שלו (26/09/2026).
  if (user?.role === 'PM') return go("/app/project");
  // מנהל הייצור נוחת על לוח הייצור (משימה 12, 25/09/2026).
  // „הבוקר" — חמש שאלות, פעולה בכל שורה (אלי 19/10/2026, המלצה 6). הלוח במרחק לחיצה.
  if (user?.role === 'MANAGER') return go("/app/morning");
  // אלי 31/10/2026: לא רצפת הייצור — מנהלת המשרד לשירות, הסמנכ״ל ללוח הניהול.
  if (user?.role === 'ACCOUNT_MANAGER') return go("/app/service");
  if (user?.role === 'VP') return go("/app/manage");
  // אלי 01/11/2026: חדר השיפור הוא הבית של המנכ״ל. הכניסה למערכת נוחתת על `/app`.
  // הדשבורד הישן נשאר בתפריט, ב-`/app/dashboard`.
  if (user?.role === 'CEO') return go('/app/improvement-room');
  // לפני שידוע מי מחובר — לא מורידים את קובץ הדשבורד לחינם (02/11/2026).
  if (!user) return <></>;
  return <DashboardPage />;
}

/** הדשבורד למנכ״ל, ולכל השאר — דף הבית שלהם, כמו `/app`. */
export function DashboardRoute(): JSX.Element {
  const { user } = useAuth();
  return (user?.baseRole ?? user?.role) === 'CEO' ? <DashboardPage /> : <RoleLanding />;
}

/**
 * כתובת השורש „/". לכולם — לוח הבקרה, כמו עד היום. המחסנאי נחת שם על לוח
 * בעיות הייצור, שאין לו בו מה לעשות (הרצה 05/10/2026) — הוא הולך הביתה.
 */
export function RootRedirect(): JSX.Element {
  const { user, isLoading } = useAuth();
  // לפני שההתחברות נטענה אין תפקיד — והפניה עכשיו הייתה שולחת גם את המחסנאי ללוח הבקרה.
  if (isLoading) return <></>;
  const role = user ? user.baseRole ?? user.role : null;
  // אלי 01/11/2026: חדר השיפור הוא הבית של המנכ״ל.
  const home = role === 'CEO' ? '/app/improvement-room' : role === 'WAREHOUSE' ? '/app/warehouse' : role === 'ACCOUNT_MANAGER' ? '/app/service' : role === 'VP' ? '/app/manage' : '/app/production-center';
  return <RouterNavigate replace to={home} />;
}
